import React, { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { orderBy } from "lodash";
import { useLocalStorage } from "usehooks-ts";

import { usePageTitle } from "@/atoms/page-title-atom";
import salesChartLoading from "@assets/sales-v3-chart-loading.gif";
import { TitleSlide } from "@components/title-slide-view";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { CreateBreakdownModal } from "@features/breakdown-v2/create-breakdown-modal";
import { TutorialHeader } from "@features/breakdown-v2/create-breakdown-modal/tutorial-header";
import { GET_LABEL_RULE_GROUPS_V3 } from "@gql/label-rules-v3";
import { useNorthbeamQuery } from "@hooks/http";
import { type GetLabelRuleGroupsV3 } from "@north-beam/nb-client-side";

import { useFilters } from "../../hooks";

import { BreakdownList } from "./breakdown-list";
import { Toolbar } from "./toolbar";

export const ManageBreakdowns = () => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const [, setPageTitle] = usePageTitle();
  const { filters, setFilters } = useFilters();

  const [showTutorialHeader, setShowTutorialHeader] = useLocalStorage(
    "createBreakdownModalShowTutorialHeader",
    true,
  );

  const [isCreateBreakdownModalOpen, setIsCreateBreakdownModalOpen] =
    useState(false);

  useEffect(() => {
    setPageTitle("Manage breakdowns");
  }, [setPageTitle]);

  const { data, loading } = useNorthbeamQuery<GetLabelRuleGroupsV3>(
    GET_LABEL_RULE_GROUPS_V3,
    { useCpuIntensive: true },
  );

  const filteredBreakdowns = useMemo(() => {
    if (!data?.me.labelRuleGroupsV3) {
      return [];
    }

    return data?.me.labelRuleGroupsV3.filter((e) =>
      e.name.toLowerCase().includes(filters.search.toLowerCase()),
    );
  }, [data?.me.labelRuleGroupsV3, filters.search]);

  const sortedBreakdowns = useMemo(
    () =>
      orderBy(
        filteredBreakdowns,
        (e) =>
          filters.sorting === "updatedAt" ? e.updatedAt : e.name.toLowerCase(),
        filters.sorting === "updatedAt" ? "desc" : "asc",
      ),
    [filteredBreakdowns, filters.sorting],
  );

  return (
    <div
      className={clsx(
        "bg-white border border-gray rounded-b-lg mt-[-1px] p-4 space-y-4",
        "h-[calc(100vh-56px-40px-48px)]",
      )}
    >
      {showTutorialHeader && (
        <TutorialHeader
          onDismiss={() => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onClick",
              additionalActionInfo: "Hide tutorial",
            });

            setShowTutorialHeader(false);
          }}
        />
      )}
      {loading ? (
        <TitleSlide>
          <img
            src={salesChartLoading}
            alt="Chart loading gif"
            className="w-1/3 m-auto"
          />
        </TitleSlide>
      ) : (
        <>
          <h4 className="font-bold">Breakdowns</h4>
          <Toolbar
            search={filters.search}
            sorting={filters.sorting}
            onChangeSearch={(search) => {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onChange",
                additionalActionInfo: "Search breakdowns",
                data: {
                  newSearch: search,
                  pageContext: { ...filters },
                },
              });

              setFilters({ search });
            }}
            onChangeSorting={(sorting) => {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onChange",
                additionalActionInfo: "Sort breakdowns",
                data: {
                  newSorting: sorting,
                  pageContext: { ...filters },
                },
              });

              setFilters({ sorting });
            }}
            onClickAddBreakdown={() => {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onClick",
                additionalActionInfo: "Open create breakdown modal",
              });

              setIsCreateBreakdownModalOpen(true);
            }}
          />
          <div
            className={clsx("overflow-x-auto", {
              " h-[calc(100%-92px)]": !showTutorialHeader,
              " h-[calc(100%-92px-190px)]": showTutorialHeader,
            })}
          >
            <BreakdownList breakdowns={sortedBreakdowns} />
          </div>
        </>
      )}
      {isCreateBreakdownModalOpen && (
        <CreateBreakdownModal
          onClose={() => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onClick",
              additionalActionInfo: "Close create breakdown modal",
            });

            setIsCreateBreakdownModalOpen(false);
          }}
          isOpen={isCreateBreakdownModalOpen}
        />
      )}
    </div>
  );
};
